<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'petite-vue-i18n';
import { useUserStore, useQuizStore } from '../../../../../stores';
import { QuizAudioKey } from '../../../../../types/quiz';
import { ClassStudentInfo } from '../../../../../types/class';
import { audioManager } from '../../../../../utils/audioManager';

import top1 from "../../../../../assets/icons/ranking_top1.svg";
import top2 from "../../../../../assets/icons/ranking_top2.svg";
import top3 from "../../../../../assets/icons/ranking_top3.svg";
import quizLeaderboard from '../../../../../assets/audios/quiz_leaderboard.mp3'
import Shuffle from '../../../../Animation/Shuffle.vue';
import { TEXT } from '../../../../../configs/text';
import LeaderboardBG from '../../../../General/LeaderboardBG.vue';
import { utils } from '../../../../../utils';

const { t } = useI18n();
const quizStore = useQuizStore();
const userStore = useUserStore();
const topStudents = [top1, top2, top3];

function studentCorrectAnswer(student: ClassStudentInfo) {
    return student.quizQuestion?.reduce((total, studentAnswer) => {
        return total + (studentAnswer.answer.filter((answer) => answer.isCorrect === 1).length === studentAnswer.correctAnswers.length ? 1 : 0)
    }, 0) ?? 0;
}

onMounted(() => {
    quizStore.updateLeaderboard()
    audioManager.play(quizLeaderboard, QuizAudioKey.SFX)
})

onBeforeUnmount(() => {
    audioManager.stop(QuizAudioKey.SFX)
})

const emit = defineEmits(['back']);
</script>
<template>
    <div class="relative w-full h-full rounded-lg pt-5 bg-white dark:bg-slate-800">
        <LeaderboardBG :title="t(TEXT.quiz_ranking)" :darkMode="quizStore.darkMode"
            class="absolute z-10 -top-5 left-1/2 -translate-x-1/2  w-[372px] h-[39px]">
        </LeaderboardBG>
        <div class="flex justify-center items-start w-full px-3 h-full overflow-auto">
            <table class="table-auto w-full">
                <thead class="sticky top-0 bg-white dark:bg-slate-800 z-10">
                    <tr class=" text-nowrap">
                        <th class="p-5 first-letter:capitalize">{{ t(TEXT.top) }}</th>
                        <th class="p-5 text-left first-letter:capitalize">{{ t(TEXT.student_name) }}</th>
                        <th class="p-5 first-letter:capitalize">{{ t(TEXT.correct_answer) }}</th>
                        <th class="p-5 first-letter:capitalize">{{ t(TEXT.time) }}</th>
                        <th class="p-5 first-letter:capitalize">{{ t(TEXT.quiz_point) }}</th>
                    </tr>
                </thead>
                <Shuffle tag="tbody">
                    <tr v-for="(student, index) in quizStore.leaderBoard"
                        :class="[{ 'text-orange-500 dark:text-fuchsia-600 bg-orange-100 dark:bg-purple-100 font-bold sticky top-14 bottom-0 z-10': student.username === userStore.username }]"
                        :key="student.id">

                        <td class="px-5 py-3 text-center"
                            :class="{ 'rounded-l-2xl': student.username === userStore.username }">
                            <img v-if="index <= 2 && quizStore.studentTotalPoint(student)" :src="topStudents[index]"
                                alt="Top 1" class="w-8 h-8 inline-block">
                            <span class="h-8 flex items-center justify-center w-full" v-else> {{
                                quizStore.studentTotalPoint(student) ? index + 1 : '-' }}</span>
                        </td>

                        <td class="px-5 py-3">
                            {{ student.full_name }}
                        </td>

                        <td class="px-5 py-3">
                            <div
                                class=" w-full bg-neutral-200 dark:bg-fuchsia-200 rounded-full h-5 relative text-center text-white dark:text-zinc-700 font-bold">
                                <div class="transistion-transform duration-[1s] h-full ease-linear p-0.5 leading-none bg-lime-500 dark:bg-fuchsia-500 rounded-full"
                                    :style="`width:${studentCorrectAnswer(student) / (quizStore.quiz?.questions?.length ?? 1) * 100}%`"
                                    v-if="studentCorrectAnswer(student) / (quizStore.quiz?.questions?.length ?? 1) * 100 > 0">
                                </div>
                                <span class="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">{{
                                    `${studentCorrectAnswer(student)}/${quizStore.quiz?.questions?.length}` }}</span>

                            </div>
                        </td>
                        <td class="px-5 py-3 text-center">
                            {{ utils.formatSecondsToMinuteSeconds(quizStore.studentAnswerTime(student), 'mmms') }}
                        </td>
                        <td class="px-5 py-3 text-center"
                            :class="{ 'rounded-r-2xl': student.username === userStore.username }">
                            {{ `${quizStore.studentTotalPoint(student)} pts` }}
                        </td>
                    </tr>
                </Shuffle>
            </table>
        </div>
    </div>
</template>